import React from "react";
import { createEmptyDrill } from "./mountingDrillUtils";

function patchNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function MountingDrillingsPanel({
  mounting,
  setMounting,
  selectedDrillId,
  setSelectedDrillId,
}) {
  const drillings = Array.isArray(mounting?.drillings) ? mounting.drillings : [];
  const selectedDrill =
    drillings.find((d) => String(d.id) === String(selectedDrillId)) || null;

    function getAxisForPlane(plane) {
  if (plane === "xy") return { x: 0, y: 0, z: 1 };
  if (plane === "xz") return { x: 0, y: 1, z: 0 };
  if (plane === "yz") return { x: 1, y: 0, z: 0 };
  return { x: 0, y: 0, z: 1 };
}

function getStartForPlane(plane, offset = 0) {
  if (plane === "xy") return { x: 0, y: 0, z: offset };
  if (plane === "xz") return { x: 0, y: offset, z: 0 };
  if (plane === "yz") return { x: offset, y: 0, z: 0 };
  return { x: 0, y: 0, z: offset };
}

function patchWorkPlane(patch) {
  setMounting((prev) => ({
    ...prev,
    workPlane: {
      ...(prev?.workPlane || {
        plane: "xy",
        offset: 0,
        lockDrillAxis: true,
      }),
      ...patch,
    },
  }));
}

function applyWorkPlaneToSelectedDrill() {
  if (!selectedDrill) return;

  const plane = mounting?.workPlane?.plane || "xy";
  const offset = Number(mounting?.workPlane?.offset || 0);
  const lockDrillAxis = mounting?.workPlane?.lockDrillAxis !== false;

  const next = drillings.map((d) => {
    if (String(d.id) !== String(selectedDrill.id)) return d;

    return {
      ...d,
      start: getStartForPlane(plane, offset),
      axis: lockDrillAxis
        ? getAxisForPlane(plane)
        : d.axis || { x: 0, y: 0, z: 1 },
    };
  });

  updateDrillings(next);
}

function getWorkPlaneLabel(plane) {
  if (plane === "xy") return "XY - front/back face";
  if (plane === "xz") return "XZ - horizontal/top view";
  if (plane === "yz") return "YZ - side face";
  return "XY - front/back face";
}

  function updateDrillings(next) {
    setMounting((prev) => ({
      ...prev,
      drillings: next,
    }));
  }

 function addDrill() {
  const plane = mounting?.workPlane?.plane || "xy";
  const offset = Number(mounting?.workPlane?.offset || 0);
  const lockDrillAxis = mounting?.workPlane?.lockDrillAxis !== false;

  const drill = {
    ...createEmptyDrill(),
    start: getStartForPlane(plane, offset),
    axis: lockDrillAxis
      ? getAxisForPlane(plane)
      : { x: 0, y: 0, z: 1 },
  };

  const next = [...drillings, drill];
  updateDrillings(next);
  setSelectedDrillId(drill.id);
}

  function removeDrill(id) {
    const next = drillings.filter((d) => String(d.id) !== String(id));
    updateDrillings(next);
    if (String(selectedDrillId) === String(id)) {
      setSelectedDrillId(next[0]?.id || null);
    }
  }

  function patchSelectedDrill(path, value) {
    if (!selectedDrill) return;

    const next = drillings.map((d) => {
      if (String(d.id) !== String(selectedDrill.id)) return d;

      if (path === "name") return { ...d, name: value };
      if (path === "kind") return { ...d, kind: value };
      if (path === "diameter") return { ...d, diameter: patchNumber(value) };
      if (path === "depth") return { ...d, depth: patchNumber(value) };
      if (path === "color") return { ...d, color: value };
      if (path === "enabled") return { ...d, enabled: !!value };

      if (path.startsWith("start.")) {
        const key = path.split(".")[1];
        return {
          ...d,
          start: {
            ...(d.start || {}),
            [key]: patchNumber(value),
          },
        };
      }

      if (path.startsWith("axis.")) {
        const key = path.split(".")[1];
        return {
          ...d,
          axis: {
            ...(d.axis || {}),
            [key]: patchNumber(value),
          },
        };
      }

      return d;
    });

    updateDrillings(next);
  }

  return (
    <div className="space-y-3">

<div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm">Drilling work plane</div>
    <div className="text-xs text-slate-500">
      Ravan na kojoj postavljaš bušenja. Nova bušenja će automatski koristiti ovu ravan.
    </div>
  </div>

  <div>
    <label className="block text-xs text-slate-500 mb-1">
      Work plane
    </label>

    <select
      value={mounting?.workPlane?.plane || "xy"}
      onChange={(e) =>
        patchWorkPlane({
          plane: e.target.value,
        })
      }
      className="w-full px-2 py-1 border rounded text-sm bg-white"
    >
      <option value="xy">XY - front/back face</option>
      <option value="xz">XZ - horizontal/top view</option>
      <option value="yz">YZ - side face</option>
    </select>
  </div>

  <div>
    <label className="block text-xs text-slate-500 mb-1">
      Plane offset
    </label>

    <input
      type="number"
      value={mounting?.workPlane?.offset ?? 0}
      onChange={(e) =>
        patchWorkPlane({
          offset: Number(e.target.value),
        })
      }
      className="w-full px-2 py-1 border rounded text-sm"
    />
  </div>

  <label className="flex items-center gap-2 text-sm">
    <input
      type="checkbox"
      checked={mounting?.workPlane?.lockDrillAxis !== false}
      onChange={(e) =>
        patchWorkPlane({
          lockDrillAxis: e.target.checked,
        })
      }
    />
    Lock drilling axis perpendicular to work plane
  </label>

  <div className="rounded bg-slate-50 border p-2 text-xs text-slate-600">
    Current:{" "}
    <span className="font-semibold">
      {getWorkPlaneLabel(mounting?.workPlane?.plane || "xy")}
    </span>
    {" "}· offset {Number(mounting?.workPlane?.offset || 0)} mm
  </div>

  {selectedDrill && (
    <button
      type="button"
      onClick={applyWorkPlaneToSelectedDrill}
      className="w-full px-3 py-2 border rounded bg-blue-50 text-blue-700 text-sm"
    >
      Apply work plane to selected drilling
    </button>
  )}
</div>

      <div className="border rounded p-3 bg-white space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Drillings</div>
          <button
            type="button"
            onClick={addDrill}
            className="px-3 py-1 border rounded text-sm bg-slate-50"
          >
            + Add drilling
          </button>
        </div>

        <div className="space-y-2">
          {drillings.map((d) => (
            <div
              key={d.id}
              className={[
                "border rounded p-2 text-sm cursor-pointer",
                String(d.id) === String(selectedDrillId)
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200 bg-white",
              ].join(" ")}
              onClick={() => setSelectedDrillId(d.id)}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium">{d.name || d.id}</div>
                  <div className="text-xs text-slate-500">
                    Ø{d.diameter} · depth {d.depth}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeDrill(d.id);
                  }}
                  className="px-2 py-1 border rounded text-red-600"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {!drillings.length && (
            <div className="text-sm text-slate-400">No drillings yet.</div>
          )}
        </div>
      </div>

      {selectedDrill && (
        <div className="border rounded p-3 bg-white space-y-3">
          <div className="font-semibold text-sm">Selected drilling</div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">Name</label>
            <input
              value={selectedDrill.name || ""}
              onChange={(e) => patchSelectedDrill("name", e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Kind</label>
              <select
                value={selectedDrill.kind || "blind"}
                onChange={(e) => patchSelectedDrill("kind", e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                <option value="blind">blind</option>
                <option value="through">through</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-slate-500 mb-1">Color</label>
              <input
                value={selectedDrill.color || "#3b82f6"}
                onChange={(e) => patchSelectedDrill("color", e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Diameter</label>
              <input
                type="number"
                value={selectedDrill.diameter ?? 5}
                onChange={(e) => patchSelectedDrill("diameter", e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 mb-1">Depth</label>
              <input
                type="number"
                value={selectedDrill.depth ?? 10}
                onChange={(e) => patchSelectedDrill("depth", e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm"
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-slate-500 mb-2">Start point</div>
            <div className="grid grid-cols-3 gap-2">
              {["x", "y", "z"].map((k) => (
                <div key={k}>
                  <label className="block text-xs text-slate-500 mb-1">{k.toUpperCase()}</label>
                  <input
                    type="number"
                    value={selectedDrill?.start?.[k] ?? 0}
                    onChange={(e) => patchSelectedDrill(`start.${k}`, e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-slate-500 mb-2">Axis</div>
            <div className="grid grid-cols-3 gap-2">
              {["x", "y", "z"].map((k) => (
                <div key={k}>
                  <label className="block text-xs text-slate-500 mb-1">{k.toUpperCase()}</label>
                  <input
                    type="number"
                    value={selectedDrill?.axis?.[k] ?? (k === "z" ? 1 : 0)}
                    onChange={(e) => patchSelectedDrill(`axis.${k}`, e.target.value)}
                    className="w-full px-2 py-1 border rounded text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selectedDrill.enabled !== false}
              onChange={(e) => patchSelectedDrill("enabled", e.target.checked)}
            />
            Enabled
          </label>
        </div>
      )}
    </div>
  );
}